import { ArtefactKind } from "@/services/backend/artefacts/kind";
import { type Process } from "@/services/backend/processes/process/process";
import type { ListRequest } from "@/services/backend/processes/process/service";
import { useProcessesListQuery } from "@/services/backend/processes/process/service";
import { Label, Remark } from "@/services/backend/processes/state/state";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  DataTable,
  DataTableContainer,
  useDataTableControllerWithSorting,
} from "@/shared/components/data-table";
import { CustomerName } from "@/shared/components/domain/customer/customer-name";
import { ServiceObjectName } from "@/shared/components/domain/customer/service-object-name";
import {
  milestoneIcon,
  MilestoneIcon,
} from "@/shared/components/domain/processes/milestone-label";
import { processMilestoneLink } from "@/shared/components/domain/processes/process-milestone-link";
import { ProcessNumber } from "@/shared/components/domain/processes/process-number";
import { ProcessStateLabel } from "@/shared/components/domain/processes/state-label";
import { Button } from "@/shared/components/ui/button";
import { ColumnSelection } from "@/shared/components/ui/column-selection-component";
import { PaginationLimitOffset } from "@/shared/components/ui/pagination-limit-offset";
import { formatDate, formatTime } from "@/shared/utilities/date";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon, LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router";
import { MilestoneFilter } from "./milestone-filter";
import { ResetFilters } from "./reset-filters";
import { StateFilter } from "./state-filter";

const tableColumns: ColumnDef<Process>[] = [
  {
    id: "Vorgangs-Nr.",
    accessorKey: "processNumber",
    header: "Vorgangs-Nr.",
    cell: ({ row }) => {
      const processData = row.original;
      if (
        processData.state.state === Label.Archived &&
        processData.state.remark === Remark.Canceled
      ) {
        return (
          <ProcessNumber
            className="ml-3 font-semibold line-through"
            processId={processData.id}
          />
        );
      }

      return (
        <NavLink to={processMilestoneLink(processData)}>
          <Button variant="ghost" size="sm" className="-mt-1.5 -mb-2.5">
            <ProcessNumber
              className="font-semibold text-teal-600"
              processId={processData.id}
            />
          </Button>
        </NavLink>
      );
    },
  },
  {
    id: "description",
    header: "Objekt",
    accessorKey: "deliveryId",
    cell: ({ row }) => {
      if (
        row.original.state.state === Label.Archived &&
        row.original.state.remark === Remark.Canceled
      ) {
        return (
          <ProcessStateLabel
            className="font-semibold"
            status={row.original.state.state}
            remark={row.original.state.remark}
          />
        );
      }
      return (
        <ServiceObjectName serviceObjectId={row.original.serviceObjectId} />
      );
    },
  },
  {
    id: "currentMilestone",
    header: "Aktueller Meilenstein",
    accessorKey: "currentMilestone",
    cell: ({ row }) => {
      const icon: LucideIcon =
        milestoneIcon[row.original.currentMilestone as ArtefactKind];
      return <MilestoneIcon icon={icon} />;
    },
  },
  {
    id: "Kunde",
    accessorKey: "customerId",
    header: "Kunde",
    cell: ({ row }) => {
      const processData = row.original;
      return <CustomerName customerId={processData.customerId} />;
    },
  },
  {
    id: "createdAt",
    header: ({ column }) => (
      <Button
        className="p-0"
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Erstellungsdatum
        {column.getIsSorted() === "asc" && (
          <ArrowUpIcon className="ml-2 h-4 w-4" />
        )}
        {column.getIsSorted() === "desc" && (
          <ArrowDownIcon className="ml-2 h-4 w-4" />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      const deliveryData = row.original;
      return (
        <div className="flex flex-col">
          <span className="font-bold">
            {formatDate(deliveryData.createdAt!)}
          </span>
          <span>{formatTime(deliveryData.createdAt!)}</span>
        </div>
      );
    },
  },
];

export function ProcessesListTable() {
  const [request, setRequest] = useState<ListRequest>(defaultRequest());
  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useProcessesListQuery(request, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const { table, sorting } = useDataTableControllerWithSorting(
    tableColumns,
    list?.items || [],
    [{ id: "createdAt", desc: true }],
  );
  useEffect(() => {
    setRequest((prev) => ({
      ...prev,
      orderBy: sorting.map((s) => ({
        field: s.id,
        dsc: s.desc,
      })),
    }));
  }, [sorting]);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="flex w-full flex-col space-y-2">
      <div className="flex justify-between space-x-2">
        <div className="flex space-x-2">
          <MilestoneFilter
            filter={request.currentMilestone!}
            onChange={(value) =>
              setRequest({ ...request, currentMilestone: value })
            }
          />
          <StateFilter
            filter={request.state!}
            onChange={(value) => setRequest({ ...request, state: value })}
          />
          <ResetFilters onClick={() => setRequest(defaultRequest())} />
        </div>
        <ColumnSelection table={table} />
      </div>
      <DataTableContainer>
        <DataTable
          table={table}
          columns={tableColumns}
          isLoading={isLoading || isFetching}
        />
      </DataTableContainer>
      <div className="flex flex-row justify-end">
        {list && (
          <PaginationLimitOffset
            offset={request.offset!}
            limit={request.limit!}
            totalCount={list.count}
            setLimitOffset={(limit, offset) =>
              setRequest({ ...request, limit, offset })
            }
          />
        )}
      </div>
    </div>
  );
}

const defaultRequest = (): ListRequest => ({
  currentMilestone: {
    active: true,
    values: [],
  },
  state: {
    active: true,
    values: [Label.Running],
  },
  orderBy: [
    {
      field: "createdAt",
      dsc: true,
    },
  ],
  limit: 10,
  offset: 0,
});
